/** Expliciet dezelfde variant koppelen; namen en artikelnummers zijn alleen zoekhulpen (US-043). */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import { useSession } from '../session/SessionContext.js';
import { Button, Card, Chip, Notice, ui } from '../components/ui/index.js';
import styles from './ProductsScreen.module.css';

interface BronProduct {
  id: string;
  name: string;
  ownSku: string;
  lengthLabel: string | null;
  color: string | null;
  foil: string | null;
  gekoppeld: boolean;
}
interface FeedArtikel {
  sku: string;
  name: string | null;
  lengteSuggestie: string | null;
  kleur: string | null;
  productId: string | null;
}

export function SharedProductLinker({
  onLinked,
  onClose,
}: {
  onLinked: (melding: string) => Promise<void>;
  onClose: () => void;
}): React.JSX.Element {
  const { activeCompany } = useSession();
  const [feedFout, setFeedFout] = useState<string | null>(null);
  const [feedLaden, setFeedLaden] = useState(false);
  const [bedrijven, setBedrijven] = useState<Array<{ id: string; name: string }>>([]);
  const [bedrijfId, setBedrijfId] = useState('');
  const [producten, setProducten] = useState<BronProduct[]>([]);
  const [feed, setFeed] = useState<FeedArtikel[]>([]);
  const [keuzes, setKeuzes] = useState<Record<string, string>>({});
  const [zoek, setZoek] = useState('');
  const [bevestigd, setBevestigd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [laden, setLaden] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let geldig = true;
    void api
      .get<{ bedrijven: Array<{ id: string; name: string }> }>('/api/monitoring/bedrijven')
      .then((data) => {
        if (geldig) setBedrijven(data.bedrijven);
      })
      .catch((e) => {
        if (geldig)
          setError(e instanceof ApiError ? e.message : 'De bedrijven konden niet worden geladen.');
      })
      .finally(() => {
        if (geldig) setLaden(false);
      });
    return () => {
      geldig = false;
    };
  }, []);
  useEffect(() => {
    if (!bedrijfId) return;
    let geldig = true;
    setLaden(true);
    setError(null);
    setFeedFout(null);
    setProducten([]);
    setFeed([]);
    setKeuzes({});
    setBevestigd(false);
    void Promise.allSettled([
      api.get<{ producten: BronProduct[] }>(
        `/api/monitoring/producten?bedrijfId=${encodeURIComponent(bedrijfId)}`,
      ),
      api.get<{ entries: FeedArtikel[] }>('/api/feed/catalogus'),
    ])
      .then(([bron, catalogus]) => {
        if (!geldig) return;
        if (bron.status === 'fulfilled') setProducten(bron.value.producten);
        else
          setError(
            bron.reason instanceof ApiError
              ? bron.reason.message
              : 'De producten van het bronbedrijf konden niet worden geladen.',
          );
        if (catalogus.status === 'fulfilled') setFeed(catalogus.value.entries);
        else
          setFeedFout(
            catalogus.reason instanceof ApiError
              ? catalogus.reason.message
              : 'De eigen XML-feed kon niet worden geladen.',
          );
      })
      .finally(() => {
        if (geldig) setLaden(false);
      });
    return () => {
      geldig = false;
    };
  }, [bedrijfId]);
  async function herstelFeed() {
    setFeedLaden(true);
    try {
      const catalogus = await api.get<{ entries: FeedArtikel[] }>('/api/feed/catalogus');
      setFeed(catalogus.entries);
      setFeedFout(null);
    } catch (e) {
      setFeedFout(e instanceof ApiError ? e.message : 'De eigen XML-feed kon niet worden geladen.');
    } finally {
      setFeedLaden(false);
    }
  }
  const geselecteerd = Object.entries(keuzes);
  const volledig =
    geselecteerd.length > 0 &&
    geselecteerd.every(([, sku]) =>
      feed.some((f) => f.sku.toLowerCase() === sku.trim().toLowerCase()),
    ) &&
    new Set(geselecteerd.map(([, sku]) => sku.trim().toLowerCase())).size === geselecteerd.length;
  async function koppel() {
    setBusy(true);
    setError(null);
    try {
      const resultaat = await api.post<{ melding: string }>('/api/monitoring/koppelen', {
        bedrijfId,
        producten: geselecteerd.map(([productId, sku]) => ({ productId, sku })),
        bevestigd,
      });
      await onLinked(resultaat.melding);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Koppelen is niet gelukt.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card
      title="Bestaand gemonitord product koppelen"
      description="Kies eerst het bronbedrijf. Koppel daarna ieder product aan het juiste artikel uit de XML-feed van uw huidige bedrijf."
      actions={
        <Button disabled={busy} onClick={onClose}>
          Sluiten
        </Button>
      }
    >
      {error && <Notice tone="error">{error}</Notice>}
      <label className={ui.field}>
        Uit welk bedrijf?
        <select
          className={ui.input}
          value={bedrijfId}
          disabled={busy}
          onChange={(e) => setBedrijfId(e.target.value)}
        >
          <option value="">Kies een bedrijf</option>
          {bedrijven.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
      {!laden && bedrijven.length === 0 && (
        <p className={ui.hint}>
          Er zijn geen andere actieve bedrijven waarvoor u beheerrechten heeft.
        </p>
      )}
      {laden && <p role="status">Gegevens ophalen…</p>}
      {bedrijfId && feedFout && (
        <Notice tone="error">
          Eigen XML-feed van {activeCompany?.companyName ?? 'het huidige bedrijf'}: {feedFout}
          <p>
            Controleer de XML-feed bij Instellingen. De producten van het bronbedrijf blijven
            hieronder zichtbaar; koppelen kan zodra de eigen feed beschikbaar is.
          </p>
          <Button busy={feedLaden} disabled={laden || busy} onClick={() => void herstelFeed()}>
            XML-feed opnieuw ophalen
          </Button>
        </Notice>
      )}
      {bedrijfId && !laden && !error && (
        <>
          <div className={styles.toolbar}>
            <input
              className={styles.search}
              type="search"
              aria-label="Zoeken in gemonitorde producten"
              placeholder="Zoek bijvoorbeeld op Unipanel, naam of artikelnummer"
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
            />
          </div>
          <datalist id="gedeelde-monitoring-feed">
            {feed.map((f) => (
              <option key={f.sku} value={f.sku}>
                {f.name ?? f.sku} — {f.lengteSuggestie ?? 'lengte onbekend'} —{' '}
                {f.kleur ?? 'kleur onbekend'}
                {f.productId ? ' (bestaand product)' : ''}
              </option>
            ))}
          </datalist>
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>Selectie</th>
                  <th>Product bij bronbedrijf</th>
                  <th>Uitvoering</th>
                  <th>Artikel uit uw eigen XML-feed</th>
                </tr>
              </thead>
              <tbody>
                {producten
                  .filter((p) =>
                    `${p.name} ${p.ownSku}`.toLowerCase().includes(zoek.trim().toLowerCase()),
                  )
                  .map((p) => {
                    const gekozen = Object.hasOwn(keuzes, p.id);
                    const artikel = feed.find(
                      (f) => f.sku.toLowerCase() === (keuzes[p.id] ?? '').trim().toLowerCase(),
                    );
                    return (
                      <tr key={p.id}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`${p.name} koppelen`}
                            checked={gekozen}
                            disabled={p.gekoppeld || busy}
                            onChange={(e) => {
                              setBevestigd(false);
                              setKeuzes((h) => {
                                const next = { ...h };
                                if (e.target.checked) next[p.id] = '';
                                else delete next[p.id];
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td>
                          {p.name}
                          <div className={ui.hint}>{p.ownSku}</div>
                        </td>
                        <td>
                          {[p.lengthLabel, p.color, p.foil].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td>
                          {p.gekoppeld ? (
                            <Chip tone="success">Al gekoppeld</Chip>
                          ) : (
                            <>
                              <input
                                className={styles.search}
                                list="gedeelde-monitoring-feed"
                                aria-label={`Eigen XML-artikel voor ${p.name}`}
                                placeholder="Zoek of kies het eigen artikelnummer"
                                value={keuzes[p.id] ?? ''}
                                disabled={!gekozen || busy || feed.length === 0 || feedLaden}
                                onChange={(e) => {
                                  setBevestigd(false);
                                  setKeuzes((h) => ({ ...h, [p.id]: e.target.value }));
                                }}
                              />
                              {artikel && (
                                <div className={ui.hint}>
                                  {artikel.name} · {artikel.lengteSuggestie ?? 'lengte onbekend'} ·{' '}
                                  {artikel.kleur ?? 'kleur onbekend'}
                                  {artikel.productId ? ' · bestaand product wordt gekoppeld' : ''}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {producten.length === 0 && (
            <p className={ui.empty}>Dit bedrijf heeft nog geen actief gemonitorde producten.</p>
          )}
          <p className={ui.hint}>
            Concurrentbronnen en metingen worden gedeeld. Het eigen artikelnummer, de eigen
            XML-prijs en stoppen met monitoren blijven per bedrijf apart. Bestaande producten
            behouden hun eigen gegevens en historie.
          </p>
          <label className={styles.kleurCel}>
            <input
              type="checkbox"
              checked={bevestigd}
              disabled={busy || !volledig}
              onChange={(e) => setBevestigd(e.target.checked)}
            />
            Ik heb gecontroleerd dat de gekoppelde artikelen exact dezelfde uitvoering en
            verkoopeenheid hebben.
          </label>
          <div className={styles.toolbar}>
            <Button
              variant="primary"
              busy={busy}
              disabled={laden || feedLaden || !volledig || !bevestigd || geselecteerd.length > 100}
              onClick={() => void koppel()}
            >
              {geselecteerd.length} product(en) koppelen
            </Button>
            <span className={ui.hint}>
              Maximaal 100 tegelijk. Elk eigen artikelnummer kan één keer gekozen worden.
            </span>
          </div>
        </>
      )}
    </Card>
  );
}
